/**
 * About Page Component
 *
 * Displays system information, contact details, and data protection information.
 *
 * DATA SOURCES:
 * =============
 * All data is fetched from the backend '/about' endpoint (GET request).
 * The endpoint returns data from multiple database tables:
 *
 * 1. VERSION INFORMATION (aboutInfo.version, aboutInfo.environment, aboutInfo.release_date):
 *    - Primary source: Database table 'app_version'
 *      Location: backend/src/backend/db/fixtures/app_version.py
 *      To update: Edit the fixture file and reload fixtures with:
 *                 manage-project fixture load --force
 *
 *    - Fallback source: backend/pyproject.toml
 *      Location: backend/pyproject.toml (line 11)
 *      Used when database is unavailable
 *
 *    Current version: 5.3.0
 *
 * 2. CONTACT INFORMATION (aboutInfo.contact_info.*):
 *    - Source: Database table 'contact_info'
 *      Location: backend/src/backend/db/fixtures/contact_info.py
 *      Fields: phone, email, service_hours, website
 *      To update: Edit the fixture file and reload fixtures
 *
 *    Current values:
 *    - Phone: 0800-704-3876
 *    - Email: euroatende@eurofarma.com
 *    - Service Hours: Monday to Friday, from 8am to 5pm
 *    - Website: https://eurofarma.com.br/programa-lena
 *
 * 3. TRANSLATIONS:
 *    - All UI text is internationalized using react-intl-universal
 *    - Translation files:
 *      - frontend/src/locales/en_US.json (English)
 *      - frontend/src/locales/pt_BR.json (Portuguese)
 *      - frontend/src/locales/es_MX.json (Spanish)
 *
 * BACKEND ENDPOINT:
 * =================
 * Route: GET /about
 * File: backend/src/backend/routes/about.py
 *
 * Response structure:
 * {
 *   "version": "5.3.0",
 *   "environment": "homologation",
 *   "release_date": "2025-11-13T10:00:00",
 *   "release_notes": "...",
 *   "contact_info": {
 *     "phone": "0800-704-3876",
 *     "email": "euroatende@eurofarma.com",
 *     "service_hours": "Monday to Friday, from 8am to 5pm",
 *     "website": "https://eurofarma.com.br/programa-lena"
 *   }
 * }
 *
 * For detailed version management documentation, see:
 * backend/VERSION.md
 */

import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Stack,
  Paper,
  Link
} from '@mui/material';
import { fetchData, HTTP_STATUS, intl, LanguageBaseContext } from 'dmsbasereact';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import { SpecialEffect as SE } from '../../components/SpecialEffect/SpecialEffect';

export const AboutPage = () => {
  const [aboutInfo, setAboutInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [v1, setV1] = useState(false);
  const [v2, setV2] = useState(false);
  const [x9, setX9] = useState(false);
  const t2 = useRef(null);
  const clickCountRef = useRef(0);
  const { language } = useContext(LanguageBaseContext);

  const d = (s) => atob(s);

  const r1 = useCallback(() => {
    if (t2.current) clearTimeout(t2.current);
    if (v1) {
      t2.current = setTimeout(() => {
        setV1(false);
        setV2(false);
        setX9(false);
        clickCountRef.current = 0;
      }, 120000);
    }
  }, [v1]);

  const h1 = () => {
    clickCountRef.current += 1;
    if (clickCountRef.current >= 3) {
      setV1(true);
      setV2(true);
      setX9(true);
    }
  };

  /**
   * Fetch system information from backend
   * Endpoint: GET /about
   * See file header documentation for data source details
   */
  useEffect(() => {
    const fetchAboutInfo = async () => {
      try {
        const response = await fetchData('/about', 'GET', false);
        if (response.status === HTTP_STATUS.OK_200) {
          // Store response data containing version, contact info, etc.
          setAboutInfo(response.data);
        } else {
          setError('Failed to load system information');
        }
      } catch (err) {
        setError('Error loading system information');
        console.error('Error fetching about info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutInfo();
  }, []);

  useEffect(() => {
    r1();
    const h2 = () => r1();

    window.addEventListener('mousemove', h2);
    window.addEventListener('click', h2);
    window.addEventListener('keydown', h2);
    window.addEventListener('scroll', h2);

    return () => {
      if (t2.current) clearTimeout(t2.current);
      window.removeEventListener('mousemove', h2);
      window.removeEventListener('click', h2);
      window.removeEventListener('keydown', h2);
      window.removeEventListener('scroll', h2);
    };
  }, [v1, r1]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return intl.get('NotAvailable').d('N/A');
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header with Logo */}
        <Paper
          elevation={0}
          sx={{
            p: 6,
            mb: 4,
            bgcolor: 'white',
            borderRadius: 3,
            textAlign: 'center',
            borderBottom: '4px solid',
            borderColor: 'primary.main'
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
            <img src="/logo-lena-512.png" alt={intl.get('AppName').d('Lena')} style={{ height: '120px', marginRight: '16px' }} />
          </Box>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            {intl.get('PharmaceuticalSystem').d('Pharmaceutical Management System')}
          </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
            {/* Version from: backend/src/backend/db/fixtures/app_version.py */}
            {intl.get('Version').d('Version')} {aboutInfo?.version} | {aboutInfo?.environment}
          </Typography>
        </Paper>



        {/* Contact Information - Source: backend/src/backend/db/fixtures/contact_info.py */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                {intl.get('ContactUs').d('Contact Us')}
              </Typography>

              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {intl.get('EurofarmaContactCenter').d('Eurofarma Customer Service')}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('Phone').d('Phone')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {aboutInfo?.contact_info?.phone || '0800-704-3876'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center">
                  <EmailIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('Email').d('Email')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                      {aboutInfo?.contact_info?.email || 'euroatende@eurofarma.com'}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" alignItems="start">
                  <LocationOnIcon sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {intl.get('ServiceHours').d('Service Hours')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {aboutInfo?.contact_info?.service_hours || intl.get('ServiceHoursDescription').d('Monday to Friday, from 8am to 5pm.')}
                    </Typography>
                  </Box>
                </Box>

                {aboutInfo?.contact_info?.website && (
                  <Box display="flex" alignItems="center">
                    <LinkIcon sx={{ color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {intl.get('Website').d('Website')}
                      </Typography>
                      <Link href={aboutInfo.contact_info.website} target="_blank" rel="noopener" variant="body1" fontWeight={500}>
                        {aboutInfo.contact_info.website}
                      </Link>
                    </Box>
                  </Box>
                )}
              </Stack>


            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}
            >
              <Typography variant="h5" fontWeight={600} mb={3}>
                {intl.get('DataSecurity').d('Data Security & Privacy')}
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {intl.get('LGPDCompliance').d('LGPD Compliance')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                    {intl.get('LGPDText').d(
                      'We are fully compliant with the Brazilian General Data Protection Law (LGPD - Lei Geral de Proteção de Dados). All patient and healthcare professional data is encrypted, stored securely, and processed according to the highest privacy standards.'
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {intl.get('DataProtection').d('Data Protection')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                    {intl.get('DataProtectionText').d(
                      'Our system employs industry-standard encryption (AES-256), secure authentication protocols, regular security audits, and continuous monitoring to protect sensitive pharmaceutical and patient information.'
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {intl.get('YourRights').d('Your Rights')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                    {intl.get('YourRightsText', {
                      email: aboutInfo?.contact_info?.email || 'euroatende@eurofarma.com'
                    }).d(
                      "You have the right to access, correct, or delete your personal data. For privacy-related inquiries, contact our Data Protection Officer at {email}"
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* System Information - Source: backend/src/backend/db/fixtures/app_version.py */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white'
          }}
        >
          <Typography variant="h5" fontWeight={600} mb={3}>
            {intl.get('SystemInformation').d('System Information')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get('Version').d('Version')}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {aboutInfo?.version || intl.get('NotAvailable').d('N/A')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get('Environment').d('Environment')}
              </Typography>
              <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                {aboutInfo?.environment || intl.get('NotAvailable').d('N/A')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" color="text.secondary">
                {intl.get('ReleaseDate').d('Release Date')}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(aboutInfo?.release_date)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {v1 && (
          <Paper
            elevation={0}
            onClick={() => {
              setV1(false);
              setV2(false);
              setX9(false);
              clickCountRef.current = 0;
            }}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, gap: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                {intl.get('DevelopedByTitle').d('This project was developed by')}
              </Typography>
              {x9 && (
                <Box sx={{ width: '60px' }}>
                  <SE />
                </Box>
              )}
            </Box>

            {v2 && <Grid container spacing={2}>
              {[
                'QW5kcmUgTHVpeiBUcmFnYW5jaW4gRmlsaG8=',
                'SWxkb21hciBHb21lcyBEZSBDYXJ2YWxobyBKdW5pb3I=',
                'SmVhbiBNYXJjZWxvIE1pcmEgSnVuaW9y',
                'THVhbmEgQXBhcmVjaWRhIEdvbWVz',
                'TWFyaWFuZSBWaWxhcyBCb2Fz',
                'TWFyY29zIFZhbGRlY2lyIENhdmFsaGVpcm8gSnVuaW9y',
                'UGVyaW9uIEZlcnJlaXJhIERlbWFtYW4=',
                'UmFmYWVsIERhIFZlaWdhIENhYnJhbA==',
                'WWFzbWluIERldmVnaWxp',
              ].map((m, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="body1" fontWeight={500} color="primary">
                      {d(m)}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>}
          </Paper>
        )}

        {/* Footer */}
        <Box textAlign="center" mt={6} pb={4}>
          <Typography variant="body2" color="text.secondary">
            ® {intl.get('EurofarmaCopyright').d('Eurofarma')} {new Date().getFullYear()}. {intl.get('AllRightsReserved').d('All rights reserved.')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {intl.get('DevelopedWithPart1').d('Developed with ')}
            <span onClick={h1} style={{ cursor: 'default', userSelect: 'none' }}>
              {intl.get('DevelopedWithCare').d('care')}
            </span>
            {intl.get('DevelopedWithPart2').d(' for healthcare professionals')}
          </Typography>

        </Box>
      </Container>
    </Box>
  );
};
